import { BOOT_TELEMETRY_URL } from "../config/appConfig";
import { getDeviceId } from "../lib/deviceStorage";
import { awaitPostUpdateHandoff } from "../sync/apiClient";
import { traceAndFetch } from "../sync/apiTrace"; // v3.2.08

export const BOOT_TELEMETRY_PENDING_KEY = "fz:bootTelemetryPending";
export const BOOT_TELEMETRY_DEBUG_KEY = "fz:bootTelemetryDebug";

let bootTelemetryFlushPromise = null;
let bootTelemetryBackoffUntil = 0;
let bootTelemetryLastFlushAt = 0;
const BOOT_TELEMETRY_MIN_FLUSH_INTERVAL_MS = 2 * 60 * 1000;
const BOOT_TELEMETRY_BACKOFF_MS = 5 * 60 * 1000;

export function readBootTelemetryQueue() {
  try {
    const raw = JSON.parse(localStorage.getItem(BOOT_TELEMETRY_PENDING_KEY) || "[]");
    return Array.isArray(raw) ? raw : [];
  } catch (e) {
    return [];
  }
}

export function writeBootTelemetryQueue(items) {
  try {
    localStorage.setItem(BOOT_TELEMETRY_PENDING_KEY, JSON.stringify(Array.isArray(items) ? items.slice(-60) : []));
  } catch (e) {}
}

export function queueBootTelemetryRecord(record) {
  if (!record || !record.boot_id) return;
  const items = readBootTelemetryQueue().filter((item) => String(item?.boot_id || "") !== String(record.boot_id));
  items.push(record);
  writeBootTelemetryQueue(items);
}

export async function flushBootTelemetryToServer(session, { force = false, apiSecret = "" } = {}) {
  const now = Date.now();
  if (bootTelemetryFlushPromise) return bootTelemetryFlushPromise;
  if (!force && bootTelemetryBackoffUntil > now) return false;
  if (!force && bootTelemetryLastFlushAt && (now - bootTelemetryLastFlushAt) < BOOT_TELEMETRY_MIN_FLUSH_INTERVAL_MS) return false;

  bootTelemetryFlushPromise = (async () => {
    const accountId = String(session?.id || "").trim();
    const userId = String(session?.userId || "").trim();
    const username = String(session?.username || "").trim();
    if (!accountId && !userId && !username) return false;
    if (!force && typeof navigator !== "undefined" && navigator && navigator.onLine === false) return false;
    const pending = readBootTelemetryQueue();
    if (!pending.length) return true;
    await awaitPostUpdateHandoff(4200);
    let okCount = 0;
    const failed = [];
    for (const item of pending.slice(0, 5)) {
      try {
        const payload = {
          ...item,
          account_id: item?.account_id || accountId,
          user_id: item?.user_id || userId,
          username: item?.username || username,
          user_name: item?.user_name || String(session?.name || session?.username || item?.user_name || ""),
        };
        // v3.9.05: não enviar telemetria de boot durante snapshot do bootstrap.
        // A rede precisa ficar exclusiva do pull_farm_bundle nesse momento.
        try {
          const _ex = typeof window !== "undefined" ? window.__fzBootstrapExclusiveMode : null;
          if (_ex?.active && _ex?.phase === "snapshot") {
            try { console.warn("[v3.9.05] boot-telemetry pausado — bootstrap snapshot ativo"); } catch (_) {}
            return;
          }
        } catch (_) {}
        const res = await traceAndFetch(BOOT_TELEMETRY_URL, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "X-API-Key": apiSecret || "",
            "X-Device-ID": getDeviceId(),
            "X-User-Name": String(session?.name || session?.username || ""),
          },
          body: JSON.stringify({ action: "upsert", item: payload }),
        }, { source: "boot-telemetry.flush", action: "upsert" });
        const json = await res.json().catch(() => null);
        if (res?.status === 429 || res?.status === 503) {
          bootTelemetryBackoffUntil = Date.now() + BOOT_TELEMETRY_BACKOFF_MS;
          throw new Error("boot telemetry rate limited");
        }
        if (!res.ok || json?.ok === false) throw new Error(json?.error || "boot telemetry failed");
        okCount += 1;
      } catch (e) {
        failed.push(item);
      }
    }
    const remaining = pending.slice(5);
    writeBootTelemetryQueue([...failed, ...remaining]);
    bootTelemetryLastFlushAt = Date.now();
    return okCount > 0 || (failed.length === 0 && remaining.length === 0);
  })().finally(() => { bootTelemetryFlushPromise = null; });

  return bootTelemetryFlushPromise;
}
